import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search, Tag } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { blogPosts, blogCategories } from '../data/blogData';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import Newsletter from '../components/Newsletter';

const BlogPage = () => {
  const { language } = useLanguage();
  const [selectedCategory, setSelectedCategory] = useState(language === 'es' ? 'Todos' : 'All');
  const [searchQuery, setSearchQuery] = useState('');
  const heroRef = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });
  
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);

  const posts = blogPosts[language] || [];
  const categories = blogCategories[language] || [];
  
  // Filter posts
  const filteredPosts = posts.filter(post => {
    const matchesCategory = selectedCategory === (language === 'es' ? 'Todos' : 'All') || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  // Get featured posts
  const featuredPosts = posts.filter(post => post.featured);

  const textRevealVariants = {
    hidden: { opacity: 0, y: 80 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.1,
        duration: 0.8,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    })
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" }
    }
  };

  const getPostUrl = (post) => {
    return language === 'es' ? `/blog/${post.slug}` : `/blog/${post.slug}`;
  };

  return (
    <main className="min-h-screen bg-[#050505] relative">
      <SEO 
        title={language === 'es' ? 'Blog & Recursos' : 'Blog & Resources'}
        description={language === 'es' 
          ? 'Artículos, guías y recursos sobre tecnología, inteligencia artificial, digitalización, ciberseguridad y más.'
          : 'Articles, guides, and resources about technology, artificial intelligence, digitalization, cybersecurity, and more.'}
        url="/blog"
      />
      <div className="grid-pattern fixed inset-0 pointer-events-none" />
      <Navbar />

      {/* Hero Section */}
      <section ref={heroRef} className="relative pt-32 pb-20 overflow-hidden">
        <motion.div 
          style={{ y: heroY }}
          className="absolute top-0 right-0 w-96 h-96 bg-[#F7931A] rounded-full blur-[200px] opacity-10" 
        />
        <motion.div 
          style={{ y: heroY }}
          className="absolute bottom-0 left-0 w-64 h-64 bg-purple-500 rounded-full blur-[150px] opacity-10" 
        />

        <motion.div 
          style={{ opacity: heroOpacity }}
          className="container-custom relative z-10"
        >
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 glass px-4 py-2 rounded-full mb-6"
            >
              <span className="text-sm font-mono text-[#F7931A] tracking-wider uppercase">
                {language === 'es' ? 'Blog & Recursos' : 'Blog & Resources'}
              </span>
            </motion.div>

            <div className="overflow-hidden mb-4">
              <motion.h1
                custom={0}
                initial="hidden"
                animate="visible"
                variants={textRevealVariants}
                className="font-['Syne'] text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter text-white"
              >
                {language === 'es' ? 'Ideas que' : 'Ideas that'}
              </motion.h1>
            </div>
            <div className="overflow-hidden mb-6">
              <motion.h1
                custom={1}
                initial="hidden"
                animate="visible"
                variants={textRevealVariants}
                className="font-['Syne'] text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter"
              >
                <span className="text-shimmer">{language === 'es' ? 'Transforman' : 'Transform'}</span>
              </motion.h1>
            </div>

            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="text-lg text-[#A1A1AA] leading-relaxed max-w-2xl mx-auto"
            >
              {language === 'es' 
                ? 'Artículos, guías y recursos sobre tecnología, inteligencia artificial, digitalización y más.'
                : 'Articles, guides, and resources about technology, artificial intelligence, digitalization, and more.'}
            </motion.p>
          </div>
        </motion.div>
      </section>

      {/* Search and Filter */}
      <section className="py-8 border-b border-[#27272A]">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row gap-6 items-center justify-between">
            {/* Search */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="relative w-full md:w-96"
            >
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#A1A1AA]" />
              <input
                type="text"
                placeholder={language === 'es' ? 'Buscar artículos...' : 'Search articles...'}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-3 glass rounded-full text-white placeholder-[#A1A1AA] focus:outline-none focus:ring-2 focus:ring-[#F7931A]/50"
                data-testid="blog-search"
              />
            </motion.div>

            {/* Categories */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex flex-wrap gap-2 justify-center"
            >
              {categories.map((category, index) => (
                <motion.button
                  key={category}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
                    selectedCategory === category
                      ? 'bg-[#F7931A] text-[#050505]'
                      : 'glass text-[#A1A1AA] hover:text-white'
                  }`}
                  data-testid={`category-${index}`}
                >
                  {category}
                </motion.button>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {selectedCategory === (language === 'es' ? 'Todos' : 'All') && searchQuery === '' && (
        <section className="py-16 border-b border-[#27272A]">
          <div className="container-custom">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-sm font-mono text-[#F7931A] tracking-widest uppercase mb-8"
            >
              {language === 'es' ? 'Artículos Destacados' : 'Featured Articles'}
            </motion.h2>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPosts.slice(0, 3).map((post, index) => (
                <Link key={post.id} to={getPostUrl(post)}>
                  <motion.article
                    initial={{ opacity: 0, y: 40 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                    whileHover={{ scale: 1.02, y: -10 }}
                    className="glass rounded-2xl overflow-hidden group cursor-pointer h-full"
                    data-testid={`featured-post-${index}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
                      <span className="absolute top-4 left-4 px-3 py-1 bg-[#F7931A] text-[#050505] text-xs font-bold rounded-full">
                        {language === 'es' ? 'Destacado' : 'Featured'}
                      </span>
                    </div>
                    <div className="p-6">
                      <span className="text-xs font-mono text-[#F7931A]">{post.category}</span>
                      <h3 className="font-['Syne'] text-xl font-bold text-white mt-2 mb-3 group-hover:text-[#F7931A] transition-colors">
                        {post.title}
                      </h3>
                      <p className="text-[#A1A1AA] text-sm line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-xs text-[#52525B]">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {post.readTime}
                        </span>
                      </div>
                    </div>
                  </motion.article>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* All Posts Grid */}
      <section className="py-16">
        <div className="container-custom">
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-sm font-mono text-[#F7931A] tracking-widest uppercase mb-8"
          >
            {selectedCategory === (language === 'es' ? 'Todos' : 'All') 
              ? (language === 'es' ? 'Todos los Artículos' : 'All Articles')
              : selectedCategory}
            {searchQuery && ` - "${searchQuery}"`}
          </motion.h2>

          {filteredPosts.length > 0 ? (
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {filteredPosts.map((post, index) => (
                <Link key={post.id} to={getPostUrl(post)}>
                  <motion.article
                    variants={staggerItem}
                    whileHover={{ scale: 1.02, y: -10 }}
                    className="glass rounded-2xl overflow-hidden group cursor-pointer h-full"
                    data-testid={`blog-post-${index}`}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img 
                        src={post.image} 
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#050505] to-transparent" />
                    </div>
                    <div className="p-6">
                      <span className="text-xs font-mono text-[#F7931A]">{post.category}</span>
                      <h3 className="font-['Syne'] text-xl font-bold text-white mt-2 mb-3 group-hover:text-[#F7931A] transition-colors line-clamp-2">
                        {post.title}
                      </h3>
                      <p className="text-[#A1A1AA] text-sm line-clamp-2 mb-4">
                        {post.excerpt}
                      </p>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4 text-xs text-[#52525B]">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {post.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {post.readTime}
                          </span>
                        </div>
                        <ArrowRight className="w-4 h-4 text-[#F7931A] opacity-0 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </motion.article>
                </Link>
              ))}
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-16"
            >
              <p className="text-[#A1A1AA] text-lg">
                {language === 'es' 
                  ? 'No se encontraron artículos con esos criterios.'
                  : 'No articles found matching those criteria.'}
              </p>
            </motion.div>
          )}
        </div>
      </section>

      {/* Newsletter Section */}
      <Newsletter />

      <Footer />
    </main>
  );
};

export default BlogPage;
